import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Edit3, Loader2, Info } from 'lucide-react';
import ProjectForm from '../../components/ProjectForm';
import projectService from '../../../../services/projectService';
import { getFirstIncompleteProjectInfoTab } from '../../utils/projectStatusUtils';
import { toast } from 'react-toastify';

export default function EditProject() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const completeSetup = searchParams.get('completeSetup') === '1';
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (projectId) {
      loadProject();
    }
  }, [projectId]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const projectData = await projectService.getProjectById(projectId!);
      console.log('Loaded project data:', projectData);
      
      // Debug: Check for project head related fields
      console.log('Project head related fields:');
      console.log('- projectHead:', projectData?.projectHead);
      console.log('- projectHeadName:', projectData?.projectHeadName);
      console.log('- assignedResources:', projectData?.assignedResources);
      console.log('- assignedResources.projectHead:', projectData?.assignedResources?.projectHead);
      
      // Debug: Check for any object fields that might cause rendering issues
      Object.entries(projectData || {}).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          console.warn(`Object found in project.${key}:`, value);
        }
      });
      
      setProject(projectData);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load project');
      navigate('/projects/projectManagment');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectUpdated = () => {
    // Navigate back to projects list after successful update
    navigate('/projects/projectManagment');
  };

  const handleBack = () => {
    navigate('/projects/projectManagment');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Project not found</p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const initialTab = completeSetup ? getFirstIncompleteProjectInfoTab(project) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen bg-gray-50"
    >
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} />
                <span className="hidden sm:inline">Back to Projects</span>
              </button>
              
              <div className="h-6 w-px bg-gray-300 hidden sm:block" />
              
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Edit3 size={18} className="text-orange-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Edit Project</h1>
                  <p className="text-sm text-gray-600 hidden sm:block">
                    {typeof project.projectName === 'string' ? project.projectName : project.name || 'Unknown Project'} ({typeof project.projectId === 'string' ? project.projectId : 'Unknown ID'})
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {completeSetup && (
          <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
            <Info className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">
              Complete the remaining project details below. You can then open this project to add assets and equipment.
            </p>
          </div>
        )}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 sm:p-8">
            <ProjectForm
              editingProject={project}
              onCreated={handleProjectUpdated}
              initialTab={initialTab ?? undefined}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
